import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { distinctUntilChanged, finalize } from 'rxjs';
import { saveAs } from 'file-saver';
import { SessionService } from 'src/app/shared/services/session.service';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import {
  DownloadAccessGroupReportRequest,
  DownloadMultipleOrganizationReportRequest,
  Organization,
} from '../../organization/organization-models';
import { DownloadChargingUserReportRequest } from '../charging-user.model';
import { OrganizationSelectorComponent } from 'src/app/shared/components/organization-selector/organization-selector.component';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import OrganizationService from 'src/app/shared/services/organization.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { ReactiveFormsModule } from '@angular/forms';

enum ReportType {
  IndividualCarReport,
  CompanyCarAndTagsReport,
  LocationReport,
  LocationAccessGroupReport,
}
@Component({
  selector: 'adm-charging-user-download-report-dialog',
  templateUrl: './charging-user-download-report-dialog.component.html',
  styleUrls: ['./charging-user-download-report-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatRadioModule,
    MatBadgeModule,
  ],
})
export class ChargingUserDownloadReportDialogComponent implements OnInit {
  data = inject<{
    organizationId: number;
    organizationName: string;
    departmentId?: number;
  }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<void, number>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private chargingUserService = inject(ChargingUserService);
  private sessionService = inject(SessionService);
  private organizationService = inject(OrganizationService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public loading = false;
  private filterModelKey = 'chargingUserDownloadReportFilterModel';
  public idTagUserType: typeof IdTagUserType = IdTagUserType;
  public reportType = ReportType;
  public organizationBadgeCount = 0;

  public downloadReportFormGroup: FormGroup = new FormGroup({
    selectedReportType: new FormControl(ReportType.CompanyCarAndTagsReport),
    organizationIds: new FormControl([] as number[]),
    startDate: new FormControl(moment().startOf('day'), [Validators.required]),
    endDate: new FormControl(moment().startOf('day'), [Validators.required]),
    organizations: new FormControl<Organization[]>([]),
  });

  async ngOnInit(): Promise<void> {
    if (this.sessionService.get(this.filterModelKey)) {
      const stateFilterModel: any = this.sessionService.get(this.filterModelKey);

      this.downloadReportFormGroup.patchValue(stateFilterModel);
    }

    this.onFormChanges();
  }

  get selectedReportType() {
    return this.downloadReportFormGroup.controls['selectedReportType'];
  }

  private onFormChanges() {
    this.downloadReportFormGroup.valueChanges.pipe(distinctUntilChanged()).subscribe(val => {
      this.sessionService.set(this.filterModelKey, val);
    });
    const sessionFilter: SessionFilter = this.sessionService.get(this.filterModelKey) as SessionFilter;
    if (sessionFilter) {
      this.downloadReportFormGroup.patchValue(sessionFilter);
      if (this.downloadReportFormGroup.controls.organizations.value) {
        this.organizationBadgeCount = this.downloadReportFormGroup.controls.organizations.value.length;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  public cancel() {
    this.dialogRef.close();
  }

  public openOrganizationSelector() {
    const dialogRef = this.dialog.open(OrganizationSelectorComponent, {
      data: {
        selectedOrganizations: this.downloadReportFormGroup.controls['organizations'].value,
      },
      width: '736px',
    });

    dialogRef.afterClosed().subscribe(organizations => {
      if (organizations) {
        this.setSelectedOrganizations(organizations);
        return;
      }
    });
  }

  resetOrganizations() {
    this.setSelectedOrganizations([]);
  }

  private setSelectedOrganizations(organizations: Organization[]): void {
    this.downloadReportFormGroup.controls['organizations'].setValue(organizations);
    this.organizationBadgeCount = organizations.length;
    this.sessionService.set(this.filterModelKey, this.downloadReportFormGroup.value);
    this.changeDetectorRef.markForCheck();
  }

  private downloadMultipleOrganizationLocationReport() {
    const downloadReportModel: DownloadMultipleOrganizationReportRequest = {
      from: moment(this.downloadReportFormGroup.value.startDate).format(),
      to: moment(this.downloadReportFormGroup.value.endDate).add(23, 'h').add(59, 'm').add(59, 's').format(),
      organizationIds: (this.downloadReportFormGroup.value.organizations as Organization[]).map(o => o.id),
    };

    this.organizationService
      .downloadMultipleOrganizationReport(downloadReportModel)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: result => {
          const fileName =
            'Forbrugsrapport for lokationer' +
            ' fra ' +
            moment(downloadReportModel.from).format('DD-MM-YYYY') +
            ' til ' +
            moment(downloadReportModel.to).format('DD-MM-YYYY') +
            '.xlsx';
          saveAs(result, fileName);
        },
      });
  }
  private downloadAccessGroupReport() {
    const downloadReportModel: DownloadAccessGroupReportRequest = {
      from: moment(this.downloadReportFormGroup.value.startDate).format(),
      to: moment(this.downloadReportFormGroup.value.endDate).add(23, 'h').add(59, 'm').add(59, 's').format(),
      organizationIds: (this.downloadReportFormGroup.value.organizations as Organization[]).map(o => o.id),
    };

    this.organizationService
      .downloadAccessGroupReport(downloadReportModel)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: result => {
          const fileName =
            'Adgangsgruppe rapport for lokationer' +
            ' fra ' +
            moment(downloadReportModel.from).format('DD-MM-YYYY') +
            ' til ' +
            moment(downloadReportModel.to).format('DD-MM-YYYY') +
            '.xlsx';
          saveAs(result, fileName);
        },
      });
  }
  public downloadReport() {
    this.loading = true;

    const downloadReportModel: DownloadChargingUserReportRequest = {
      from: moment(this.downloadReportFormGroup.value.startDate).format('YYYY-MM-DD'),
      to: moment(this.downloadReportFormGroup.value.endDate).format('YYYY-MM-DD'),
      organizationIds: (this.downloadReportFormGroup.value.organizations as Organization[]).map(o => o.id),
      idTagUserType:
        this.downloadReportFormGroup.value.selectedReportType === this.reportType.CompanyCarAndTagsReport
          ? this.idTagUserType.CompanyCar
          : this.idTagUserType.NoCompanyCar,
    };

    if (this.downloadReportFormGroup.value.selectedReportType === ReportType.LocationReport) {
      this.downloadMultipleOrganizationLocationReport();
      return;
    }

    if (this.downloadReportFormGroup.value.selectedReportType === ReportType.LocationAccessGroupReport) {
      this.downloadAccessGroupReport();
      return;
    }

    let fileNamePrefix = 'Faktureringrapport medarbejderbiler m kontonr';
    if (this.downloadReportFormGroup.value.selectedReportType === ReportType.CompanyCarAndTagsReport) {
      fileNamePrefix = 'Faktureringsrapport flÃ¥de- og firmabiler m aftalenr';
    }

    this.chargingUserService
      .downloadChargingUserReport(downloadReportModel)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: res => {
          const fileName =
            fileNamePrefix +
            ' fra ' +
            moment(downloadReportModel.from).format('DD-MM-YYYY') +
            ' til ' +
            moment(downloadReportModel.to).format('DD-MM-YYYY') +
            '.xlsx';
          saveAs(res, fileName);
        },
      });
  }
}
interface SessionFilter {
  selectedReportType: IdTagUserType;
  organizations: Organization[];
  startDate: string;
  endDate: string;
}
